import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import OnboardingTooltip from '../components/OnboardingTooltip';
import ReportsPanel from '../components/ReportsPanel';
import StockManagementPanel from '../components/StockManagementPanel';
import FuelStatusPanel from '../components/FuelStatusPanel';
import { playEmployeeNotification, areSoundsEnabled, initializeAudio } from '../utils/notificationSounds';
import { 
  Package, Clock, CheckCircle, Truck, XCircle, RefreshCw, 
  DollarSign, LogOut, User, MapPin, Phone, Mail,
  MessageSquare, ChevronRight, Loader2, LayoutDashboard,
  ClipboardList, BarChart3, Bell, AlertTriangle, Boxes,
  TrendingUp, TrendingDown, Calendar, Edit2, Save,
  Users, UserCheck, Car, Bike, ShieldCheck, Building2,
  UserCircle, ShoppingCart, Star, Award, Search, FileText, History, Camera
} from 'lucide-react';

// Onboarding steps for funcionarios
const funcionarioOnboardingSteps = [
  {
    icon: LayoutDashboard,
    title: "Bem-vindo ao Painel!",
    message: "Este é o seu centro de controlo para gerir todos os pedidos e operações da estação.",
    tips: "Use o menu lateral para navegar entre as diferentes secções.",
    bgColor: "bg-gradient-to-r from-zinc-800 to-zinc-700"
  },
  {
    icon: ClipboardList,
    title: "Gestão de Pedidos",
    message: "Veja todos os pedidos da sua estação. Acompanhe o estado de cada pedido e avance-os no fluxo: Pendente → Preparação → Pronto.",
    tips: "Pedidos 'Prontos' com entrega ficam disponíveis para os entregadores.",
    bgColor: "bg-gradient-to-r from-yellow-600 to-yellow-500"
  },
  {
    icon: Boxes,
    title: "Gestão de Stock",
    message: "Controle o inventário de cada produto na sua estação. Actualize quantidades e defina alertas de stock baixo.",
    tips: "Clique no ícone de lápis para editar a quantidade de um produto.",
    bgColor: "bg-gradient-to-r from-blue-600 to-blue-500"
  },
  {
    icon: Users,
    title: "Ver Entregadores",
    message: "Veja os entregadores disponíveis na plataforma. A aprovação de novos entregadores é feita pelo Super Admin.",
    tips: "Entregadores verificados podem aceitar entregas da sua estação.",
    bgColor: "bg-gradient-to-r from-green-600 to-green-500"
  },
  {
    icon: Bell,
    title: "Alertas de Stock",
    message: "Receba notificações quando um produto está em ruptura ou com stock baixo. Mantenha a sua estação sempre abastecida!",
    tips: "O badge vermelho indica quantos alertas activos existem.",
    bgColor: "bg-gradient-to-r from-red-600 to-red-500"
  },
  {
    icon: BarChart3,
    title: "Relatórios de Vendas",
    message: "Analise o desempenho da sua estação. Veja vendas diárias, produtos mais vendidos e evolução semanal.",
    tips: "Use estes dados para optimizar o seu stock e serviço.",
    bgColor: "bg-gradient-to-r from-purple-600 to-purple-500"
  }
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { api, user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [staffData, setStaffData] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [newStockValue, setNewStockValue] = useState('');
  const [verifyingDriver, setVerifyingDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('pending');
  const [activeNav, setActiveNav] = useState('orders');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const previousPendingCountRef = useRef(0);
  const profileInputRef = useRef(null);

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await api().post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await api().patch(`/user/profile-photo?photo_url=${encodeURIComponent(uploadRes.data.url)}`);
      toast.success('Foto de perfil actualizada');
      // Reload page to update user context
      window.location.reload();
    } catch (err) {
      toast.error('Erro ao actualizar foto');
    }
    setUploadingPhoto(false);
  };

  // Initialize audio on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      initializeAudio();
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);
    return () => document.removeEventListener('click', handleFirstInteraction);
  }, []);



  const loadData = useCallback(async () => {
    try {
      const [statsRes, ordersRes, alertsRes, driversRes] = await Promise.all([
        api().get('/admin/stats'),
        api().get('/orders'),
        api().get('/stock/alerts'),
        api().get('/admin/drivers/pending')
      ]);
      setStats(statsRes.data);
      
      // Check for new pending orders and play sound
      const newOrders = ordersRes.data;
      const pendingCount = newOrders.filter(o => o.status === 'pending').length;
      
      if (pendingCount > previousPendingCountRef.current && previousPendingCountRef.current > 0 && areSoundsEnabled()) {
        playEmployeeNotification();
      }
      previousPendingCountRef.current = pendingCount;
      
      setOrders(newOrders);
      setAlerts(alertsRes.data);
      setPendingDrivers(driversRes.data);
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (err.response?.status === 403) {
        toast.error('Acesso negado');
        navigate('/funcionario');
      }
    }
    setLoading(false);
  }, [api, navigate]);

  const loadStock = useCallback(async () => {
    if (!user?.station_id) return;
    try {
      const res = await api().get(`/stock/station/${user.station_id}`);
      setStock(res.data);
    } catch (err) {
      console.error('Error loading stock:', err);
    }
  }, [api, user?.station_id]);

  const loadAlerts = useCallback(async () => {
    try {
      const res = await api().get('/stock/alerts');
      setAlerts(res.data);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  }, [api]);

  const loadPendingDrivers = useCallback(async () => {
    try {
      // Load all verified drivers for display
      const res = await api().get('/admin/drivers');
      setPendingDrivers(res.data);
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  }, [api]);

  const loadStaff = useCallback(async () => {
    try {
      const res = await api().get('/admin/staff');
      setStaffData(res.data);
    } catch (err) {
      console.error('Error loading staff:', err);
    }
  }, [api]);

  const loadClients = useCallback(async () => {
    try {
      const res = await api().get('/admin/clients');
      setClientsData(res.data);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  }, [api]);

  const verifyDriver = async (driverId) => {
    setVerifyingDriver(driverId);
    try {
      await api().post(`/admin/drivers/verify/${driverId}`);
      toast.success('Entregador verificado com sucesso!');
      loadPendingDrivers();
      loadData();
    } catch (err) {
      toast.error('Erro ao verificar entregador');
    }
    setVerifyingDriver(null);
  };

  const loadReports = useCallback(async () => {
    try {
      const [dailyRes, weeklyRes] = await Promise.all([
        api().get('/reports/daily'),
        api().get('/reports/weekly')
      ]);
      setDailyReport(dailyRes.data);
      setWeeklyReport(weeklyRes.data);
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  }, [api]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    if (activeNav === 'stock') loadStock();
    if (activeNav === 'alerts') loadAlerts();
    if (activeNav === 'reports') loadReports();
    if (activeNav === 'drivers') loadPendingDrivers();
    if (activeNav === 'staff') loadStaff();
    if (activeNav === 'clients') loadClients();
  }, [activeNav, loadStock, loadAlerts, loadReports, loadPendingDrivers, loadStaff, loadClients]);

  const updateStock = async (productId) => {
    if (!newStockValue || isNaN(newStockValue)) {
      toast.error('Quantidade inválida');
      return;
    }
    try {
      await api().post(`/stock/update/${user.station_id}/${productId}`, {
        quantity: parseInt(newStockValue)
      });
      toast.success('Stock actualizado!');
      setEditingStock(null);
      setNewStockValue('');
      loadStock();
      loadAlerts();
    } catch (err) {
      toast.error('Erro ao actualizar stock');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(orderId);
    try {
      await api().patch(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Estado actualizado!');
      await loadData();
    } catch (err) {
      toast.error('Erro ao actualizar estado');
    }
    setUpdatingOrder(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/funcionario');
  };

  const statusConfig = {
    pending: { label: 'Pendente', color: 'bg-yellow-500', icon: Clock },
    preparing: { label: 'Em Preparação', color: 'bg-blue-500', icon: Package },
    ready: { label: 'Pronto', color: 'bg-green-500', icon: CheckCircle },
    in_delivery: { label: 'Em Entrega', color: 'bg-purple-500', icon: Truck },
    completed: { label: 'Concluído', color: 'bg-green-600', icon: CheckCircle },
    cancelled: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle },
  };

  const getNextStatus = (current) => {
    const flow = { pending: 'preparing', preparing: 'ready', ready: 'in_delivery', in_delivery: 'completed' };
    return flow[current];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-AO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return ['pending', 'preparing', 'ready', 'in_delivery'].includes(order.status);
    return order.status === activeTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-sonangol-orange border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-zinc-400">A carregar dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex">
      {/* Onboarding Tooltips for first-time funcionarios */}
      <OnboardingTooltip 
        storageKey="halubri_funcionario_onboarding_seen"
        steps={funcionarioOnboardingSteps}
      />

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 bg-zinc-800 border-r border-zinc-700 flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sonangol-orange rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-barlow font-bold text-white">HáLubri</p>
              <p className="text-xs text-zinc-400">Portal Funcionário</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="relative">
              <input 
                type="file" 
                accept="image/*" 
                ref={profileInputRef}
                onChange={handleProfilePhotoChange}
                className="hidden"
              />
              {user?.profile_photo ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`} 
                  alt={user?.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-zinc-400" />
                </div>
              )}
              <button
                onClick={() => profileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -right-1 w-5 h-5 bg-sonangol-orange rounded-full flex items-center justify-center shadow-lg hover:bg-orange-600 transition-colors"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-2.5 h-2.5 text-white animate-spin" />
                ) : (
                  <Camera className="w-2.5 h-2.5 text-white" />
                )}
              </button>
            </div>
            <div>
              <p className="text-white font-semibold">{user?.name}</p>
              <div className="bg-zinc-900 rounded-lg px-2 py-0.5 inline-block">
                <span className="text-sonangol-orange text-xs font-bold">{user?.funcionario_id}</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-400 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {user?.station_name}
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <button
            onClick={() => setActiveNav('orders')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'orders' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <ClipboardList className="w-5 h-5" />
            <span>Gestão de Pedidos</span>
          </button>
          <button
            onClick={() => setActiveNav('stock')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'stock' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Boxes className="w-5 h-5" />
            <span>Gestão de Stock</span>
          </button>
          <button
            onClick={() => setActiveNav('drivers')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative ${
              activeNav === 'drivers' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Entregadores</span>
          </button>
          <button
            onClick={() => setActiveNav('alerts')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative ${
              activeNav === 'alerts' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bell className="w-5 h-5" />
            <span>Alertas</span>
            {alerts.length > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {alerts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveNav('reports')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'reports' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>Relatórios</span>
          </button>
          <button
            onClick={() => setActiveNav('reports-download')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'reports-download' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Descarregar Relatórios</span>
          </button>
          <button
            onClick={() => setActiveNav('stock-movements')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'stock-movements' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <History className="w-5 h-5" />
            <span>Movimentos Stock</span>
          </button>
          
          {/* Separator */}
          <div className="my-2 border-t border-zinc-700" />
          <p className="px-3 text-xs text-zinc-500 uppercase tracking-wider mb-2">Gestão</p>
          
          <button
            onClick={() => setActiveNav('staff')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'staff' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <UserCircle className="w-5 h-5" />
            <span>Pessoal</span>
          </button>
          <button
            onClick={() => setActiveNav('clients')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeNav === 'clients' ? 'bg-sonangol-orange text-white' : 'text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Clientes</span>
          </button>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-zinc-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Terminar Sessão</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-zinc-800 border-b border-zinc-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sonangol-orange rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">{user?.name}</p>
                <p className="text-zinc-400 text-xs">{user?.funcionario_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Station Banner */}
        <div className="bg-gradient-to-r from-sonangol-orange to-orange-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-white/80" />
              <span className="text-white font-medium text-sm">{user?.station_name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={loadData} className="text-white/80 hover:text-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualizar
            </Button>
          </div>
        </div>

        {activeNav === 'orders' && <FuelStatusPanel />}

        {/* Stats Cards - Only show for orders view */}
        {activeNav === 'orders' && stats && (
          <div className="px-4 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-zinc-800 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-yellow-500 text-xs font-medium">Pendentes</span>
              </div>
              <p className="font-barlow font-bold text-3xl text-white">{stats.pending_orders || 0}</p>
            </div>
            
            <div className="bg-zinc-800 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-blue-500" />
                <span className="text-blue-500 text-xs font-medium">Preparação</span>
              </div>
              <p className="font-barlow font-bold text-3xl text-white">{stats.preparing_orders || 0}</p>
            </div>
            
            <div className="bg-zinc-800 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-500 text-xs font-medium">Prontos</span>
              </div>
              <p className="font-barlow font-bold text-3xl text-white">{stats.ready_orders || 0}</p>
            </div>
            
            <div className="bg-zinc-800 border border-sonangol-orange/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-sonangol-orange" />
                <span className="text-sonangol-orange text-xs font-medium">Hoje</span>
              </div>
              <p className="font-barlow font-bold text-2xl text-white">
                {(stats.today_revenue_aoa / 1000).toFixed(0)}K <span className="text-sm font-normal text-zinc-400">AOA</span>
              </p>
            </div>
          </div>
        )}

        {/* ========== ORDERS VIEW ========== */}
        {activeNav === 'orders' && (
          <>
            {/* Orders Filter Tabs */}
            <div className="px-4">
              <div className="bg-zinc-800 p-1 rounded-xl flex gap-1 overflow-x-auto">
                {[
                  { id: 'pending', label: 'Pendentes', color: 'bg-yellow-500' },
                  { id: 'preparing', label: 'Preparação', color: 'bg-blue-500' },
                  { id: 'ready', label: 'Prontos', color: 'bg-green-500' },
                  { id: 'all', label: 'Todos', color: 'bg-zinc-600' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 py-3 px-2 sm:px-3 whitespace-nowrap rounded-lg text-xs font-medium transition-colors ${
                      activeTab === tab.id ? `${tab.color} text-white` : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                    {tab.id !== 'all' && stats && (
                      <span className="ml-1">
                        ({tab.id === 'pending' ? stats.pending_orders : tab.id === 'preparing' ? stats.preparing_orders : stats.ready_orders || 0})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Orders List */}
            <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
              {filteredOrders.length === 0 ? (
                <div className="bg-zinc-800 rounded-xl p-8 text-center border border-zinc-700">
                  <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">Nenhum pedido nesta categoria</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const nextStatus = getNextStatus(order.status);
                    
                    return (
                      <div 
                        key={order.id} 
                        className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden"
                        data-testid={`admin-order-${order.id}`}
                      >
                        {/* Order Header */}
                        <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-barlow font-bold text-white">#{order.id.slice(0, 8)}</span>
                              <Badge className={`${status.color} text-white border-0 text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                              {order.is_corporate_order && (
                                <Badge className="bg-purple-600 text-white border-0 text-xs">Corporate</Badge>
                              )}
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">{formatDate(order.created_at)}</p>
                          </div>
                          <p className="font-barlow font-bold text-sonangol-orange text-lg">
                            {order.total_aoa.toLocaleString('pt-AO')} <span className="text-xs">AOA</span>
                          </p>
                        </div>
                        
                        {/* Order Items */}
                        <div className="px-4 py-3 bg-zinc-900/50">
                          <p className="text-zinc-400 text-xs mb-2">Produtos:</p>
                          <div className="space-y-1">
                            {order.items.map((item) => (
                              <div key={item.product_id} className="flex justify-between text-sm">
                                <span className="text-zinc-300">{item.quantity}x {item.name}</span>
                                <span className="text-zinc-500">{item.subtotal.toLocaleString('pt-AO')} AOA</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Delivery Info */}
                        <div className="px-4 py-3 border-t border-zinc-700">
                          {/* Customer Info */}
                          <div className="flex items-center gap-2 text-sm text-white mb-2">
                            <User className="w-4 h-4 text-sonangol-orange flex-shrink-0" />
                            <span className="font-medium">{order.customer_name || 'Cliente'}</span>
                            {order.company_name && (
                              <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 text-xs ml-1">
                                {order.company_name}
                              </Badge>
                            )}
                          </div>
                          {order.customer_phone && (
                            <div className="flex items-center gap-2 text-sm text-zinc-300 mb-2">
                              <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <a href={`tel:${order.customer_phone}`} className="tap-target hover:text-white transition-colors">
                                {order.customer_phone}
                              </a>
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-xs text-zinc-400">
                            <span className="flex items-center gap-1">
                              {order.delivery_method === 'pickup' ? <MapPin className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                              {order.delivery_method === 'pickup' ? 'Recolha' : 'Entrega'}
                            </span>
                            <span>•</span>
                            <span>{order.payment_method === 'multicaixa' ? 'Multicaixa' : order.payment_method === 'mobile_money' ? 'Mobile Money' : 'Dinheiro'}</span>
                          </div>
                          {order.address && (
                            <p className="text-zinc-500 text-xs mt-2 italic">{order.address}</p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="px-4 py-3 bg-zinc-800 border-t border-zinc-700 flex gap-2">
                          {nextStatus && order.status !== 'completed' && order.status !== 'cancelled' ? (
                            <Button
                              className={`flex-1 h-10 ${status.color} hover:opacity-90 font-barlow font-bold uppercase text-sm`}
                              onClick={() => updateOrderStatus(order.id, nextStatus)}
                              disabled={updatingOrder === order.id}
                              data-testid={`advance-order-${order.id}`}
                            >
                              {updatingOrder === order.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  Avançar para {statusConfig[nextStatus]?.label}
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </>
                              )}
                            </Button>
                          ) : (
                            <div className="flex-1 h-10 flex items-center justify-center text-zinc-500 text-sm">
                              {order.status === 'completed' ? '✓ Pedido Concluído' : order.status === 'cancelled' ? '✗ Cancelado' : ''}
                            </div>
                          )}
                          
                          <Link to={`/admin/order/${order.id}`}>
                            <Button variant="outline" size="sm" className="h-10 border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Chat
                            </Button>
                          </Link>
                          
                          {order.status !== 'completed' && order.status !== 'cancelled' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => updateOrderStatus(order.id, 'cancelled')}
                              disabled={updatingOrder === order.id}
                              data-testid={`cancel-order-${order.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ========== STOCK VIEW ========== */}
        {activeNav === 'stock' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-xl text-white">Stock da Estação</h2>
              <Button variant="outline" size="sm" onClick={loadStock} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            <div className="space-y-3">
              {stock.map((item) => (
                <div 
                  key={item.product_id}
                  className={`bg-zinc-800 rounded-xl border p-4 ${
                    item.is_out_of_stock ? 'border-red-500/50' : 
                    item.is_low_stock ? 'border-yellow-500/50' : 'border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-barlow font-bold text-white">{item.product_name}</h3>
                      <p className="text-zinc-500 text-sm">{item.product_category} · {item.product_price?.toLocaleString('pt-AO')} AOA</p>
                    </div>
                    <div className="text-right">
                      {editingStock === item.product_id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={newStockValue}
                            onChange={(e) => setNewStockValue(e.target.value)}
                            className="w-20 h-8 bg-zinc-900 border-zinc-600 text-white text-center"
                            min="0"
                          />
                          <Button size="sm" onClick={() => updateStock(item.product_id)} className="bg-green-600 hover:bg-green-700">
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingStock(null)} className="border-zinc-600">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div>
                            <p className={`font-barlow font-bold text-2xl ${
                              item.is_out_of_stock ? 'text-red-500' : 
                              item.is_low_stock ? 'text-yellow-500' : 'text-green-500'
                            }`}>
                              {item.quantity}
                            </p>
                            <p className="text-zinc-500 text-xs">unidades</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => { setEditingStock(item.product_id); setNewStockValue(item.quantity.toString()); }}
                            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Stock bar */}
                  <div className="mt-3">
                    <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          item.is_out_of_stock ? 'bg-red-500' : 
                          item.is_low_stock ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((item.quantity / (item.max_capacity || 100)) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-zinc-500 mt-1">
                      <span>Mín: {item.min_threshold}</span>
                      <span>Máx: {item.max_capacity || 100}</span>
                    </div>
                  </div>
                  
                  {/* Status badge */}
                  {(item.is_out_of_stock || item.is_low_stock) && (
                    <div className="mt-3">
                      <Badge className={`${item.is_out_of_stock ? 'bg-red-500' : 'bg-yellow-500'} text-white border-0`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {item.is_out_of_stock ? 'Ruptura de Stock' : 'Stock Baixo'}
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== ALERTS VIEW ========== */}
        {activeNav === 'alerts' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-xl text-white">Alertas de Stock</h2>
              <Button variant="outline" size="sm" onClick={loadAlerts} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            {alerts.length === 0 ? (
              <div className="bg-zinc-800 rounded-xl p-8 text-center border border-zinc-700">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-white font-medium">Tudo em ordem!</p>
                <p className="text-zinc-400 text-sm">Nenhuma alerta de stock activa</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className={`bg-zinc-800 rounded-xl border p-4 ${
                      alert.alert_type === 'out_of_stock' ? 'border-red-500/50 bg-red-500/5' : 'border-yellow-500/50 bg-yellow-500/5'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          alert.alert_type === 'out_of_stock' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                        }`}>
                          <AlertTriangle className={`w-5 h-5 ${
                            alert.alert_type === 'out_of_stock' ? 'text-red-500' : 'text-yellow-500'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-barlow font-bold text-white">{alert.product_name}</h3>
                          <p className="text-zinc-400 text-sm">{alert.station_name}</p>
                          <p className="text-zinc-500 text-xs mt-1">
                            {new Date(alert.created_at).toLocaleDateString('pt-AO', { 
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`${
                          alert.alert_type === 'out_of_stock' ? 'bg-red-500' : 'bg-yellow-500'
                        } text-white border-0`}>
                          {alert.alert_type === 'out_of_stock' ? 'RUPTURA' : 'BAIXO'}
                        </Badge>
                        <p className="text-zinc-400 text-sm mt-2">
                          Actual: <span className="font-bold text-white">{alert.current_quantity}</span>
                        </p>
                        <p className="text-zinc-500 text-xs">
                          Mínimo: {alert.min_threshold}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== REPORTS VIEW ========== */}
        {activeNav === 'reports' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-barlow font-bold text-xl text-white">Relatórios de Vendas</h2>
              <Button variant="outline" size="sm" onClick={loadReports} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            {/* Daily Summary */}
            {dailyReport && (
              <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-sonangol-orange" />
                  <h3 className="font-barlow font-bold text-white">Resumo de Hoje</h3>
                  <span className="text-zinc-500 text-sm">({dailyReport.date})</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-zinc-900 rounded-lg p-3">
                    <p className="text-zinc-400 text-xs">Total Pedidos</p>
                    <p className="font-barlow font-bold text-2xl text-white">{dailyReport.total_orders}</p>
                  </div>
                  <div className="bg-zinc-900 rounded-lg p-3">
                    <p className="text-zinc-400 text-xs">Facturação</p>
                    <p className="font-barlow font-bold text-2xl text-sonangol-orange">
                      {dailyReport.total_revenue.toLocaleString('pt-AO')} <span className="text-sm">AOA</span>
                    </p>
                  </div>
                </div>
                
                {dailyReport.top_product && (
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-400 text-xs mb-1">Produto Mais Vendido</p>
                    <p className="text-white font-medium">{dailyReport.top_product}</p>
                  </div>
                )}
                
                {dailyReport.products_sold.length > 0 && (
                  <div className="mt-4">
                    <p className="text-zinc-400 text-sm mb-2">Produtos Vendidos</p>
                    <div className="space-y-2">
                      {dailyReport.products_sold.map((p) => (
                        <div key={p.product_id || p.name} className="flex justify-between items-center text-sm">
                          <span className="text-zinc-300">{p.name}</span>
                          <div className="text-right">
                            <span className="text-white font-medium">{p.quantity} un.</span>
                            <span className="text-zinc-500 ml-2">{p.revenue.toLocaleString('pt-AO')} AOA</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Weekly Chart */}
            {weeklyReport && (
              <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <h3 className="font-barlow font-bold text-white">Últimos 7 Dias</h3>
                </div>
                
                <div className="space-y-2">
                  {weeklyReport.days.map((day, idx) => {
                    const maxRevenue = Math.max(...weeklyReport.days.map(d => d.revenue));
                    const percentage = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                    
                    return (
                      <div key={day.date} className="flex items-center gap-3">
                        <span className="text-zinc-500 text-xs w-20">
                          {new Date(day.date).toLocaleDateString('pt-AO', { weekday: 'short', day: '2-digit' })}
                        </span>
                        <div className="flex-1 h-6 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-sonangol-orange to-orange-500 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="text-right w-24">
                          <span className="text-white text-sm font-medium">{day.orders} ped.</span>
                        </div>
                        <div className="text-right w-28">
                          <span className="text-sonangol-orange text-sm">{(day.revenue / 1000).toFixed(0)}K AOA</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== DRIVERS VIEW ========== */}
        {activeNav === 'drivers' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-xl text-white">Entregadores Disponíveis</h2>
              <Button variant="outline" size="sm" onClick={loadPendingDrivers} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            {/* Info Banner */}
            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 mb-4">
              <p className="text-zinc-400 text-sm">
                A aprovação de novos entregadores é feita pelo Super Admin. 
                Aqui pode ver os entregadores verificados disponíveis para entregas.
              </p>
            </div>
            
            {/* Verified Drivers */}
            <div className="space-y-3">
              {pendingDrivers.filter(d => d.is_verified).length === 0 ? (
                <div className="bg-zinc-800 rounded-xl p-8 text-center border border-zinc-700">
                  <Truck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-white font-medium">Nenhum entregador verificado</p>
                  <p className="text-zinc-500 text-sm">Aguarde a aprovação pelo Super Admin</p>
                </div>
              ) : (
                pendingDrivers.filter(d => d.is_verified).map((driver) => (
                  <div 
                    key={driver.id}
                    className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden"
                  >
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            driver.status === 'available' ? 'bg-green-500/20' : 
                            driver.status === 'busy' ? 'bg-yellow-500/20' : 'bg-zinc-700'
                          }`}>
                            {driver.vehicle_type === 'moto' ? (
                              <Bike className="w-6 h-6 text-green-400" />
                            ) : (
                              <Truck className="w-6 h-6 text-green-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-barlow font-bold text-white">{driver.name}</p>
                            <p className="text-zinc-400 text-sm">{driver.phone}</p>
                          </div>
                        </div>
                        <Badge className={`${
                          driver.status === 'available' ? 'bg-green-600' : 
                          driver.status === 'busy' ? 'bg-yellow-600' : 'bg-zinc-600'
                        } text-white border-0`}>
                          {driver.status === 'available' ? 'Disponível' : 
                           driver.status === 'busy' ? 'Ocupado' : 'Offline'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          {driver.total_deliveries || 0} entregas
                        </span>
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          {(driver.rating || 5).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ========== STAFF VIEW ========== */}
        {activeNav === 'staff' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-xl text-white">Pessoal das Estações</h2>
              <Button variant="outline" size="sm" onClick={loadStaff} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            {/* Summary */}
            {staffData && (
              <div className="bg-zinc-800 rounded-xl p-4 mb-4 border border-zinc-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <UserCircle className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Total de Funcionários</p>
                    <p className="font-barlow text-2xl font-bold text-white">{staffData.total}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Staff by Station */}
            {staffData && Object.entries(staffData.by_station).map(([stationName, funcionarios]) => (
              <div key={stationName} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-sonangol-orange" />
                  <h3 className="font-barlow font-bold text-white">{stationName}</h3>
                  <Badge className="bg-zinc-700 text-zinc-300 border-0">{funcionarios.length}</Badge>
                </div>
                
                <div className="space-y-2">
                  {funcionarios.map((func) => (
                    <div 
                      key={func.id}
                      className="bg-zinc-800 rounded-xl border border-zinc-700 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-sonangol-orange/20 rounded-lg flex items-center justify-center">
                            <User className="w-5 h-5 text-sonangol-orange" />
                          </div>
                          <div>
                            <p className="font-barlow font-bold text-white">{func.name}</p>
                            <p className="text-sonangol-orange text-sm font-mono">{func.funcionario_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 text-zinc-400 text-sm">
                            <Phone className="w-4 h-4" />
                            <span>{func.phone || 'Sem telefone'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ========== CLIENTS VIEW ========== */}
        {activeNav === 'clients' && (
          <div className="flex-1 px-4 py-4 pb-20 lg:pb-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-xl text-white">Gestão de Clientes</h2>
              <Button variant="outline" size="sm" onClick={loadClients} className="border-zinc-600 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualizar
              </Button>
            </div>
            
            {/* Stats Summary */}
            {clientsData && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                  <p className="text-zinc-400 text-xs">Total Clientes</p>
                  <p className="font-barlow text-2xl font-bold text-white">{clientsData.total}</p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4 border border-blue-500/30">
                  <p className="text-blue-400 text-xs">Particulares</p>
                  <p className="font-barlow text-2xl font-bold text-white">{clientsData.individual_count}</p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4 border border-purple-500/30">
                  <p className="text-purple-400 text-xs">Corporate</p>
                  <p className="font-barlow text-2xl font-bold text-white">{clientsData.corporate_count}</p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4 border border-green-500/30">
                  <p className="text-green-400 text-xs">Activos</p>
                  <p className="font-barlow text-2xl font-bold text-white">{clientsData.active_clients}</p>
                </div>
              </div>
            )}
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Pesquisar por nome, email ou telefone..."
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={clientTypeFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientTypeFilter('all')}
                  className={clientTypeFilter === 'all' ? 'bg-sonangol-orange' : 'border-zinc-600 text-zinc-300'}
                >
                  Todos
                </Button>
                <Button
                  variant={clientTypeFilter === 'individual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientTypeFilter('individual')}
                  className={clientTypeFilter === 'individual' ? 'bg-blue-600' : 'border-zinc-600 text-zinc-300'}
                >
                  Particulares
                </Button>
                <Button
                  variant={clientTypeFilter === 'corporate' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientTypeFilter('corporate')}
                  className={clientTypeFilter === 'corporate' ? 'bg-purple-600' : 'border-zinc-600 text-zinc-300'}
                >
                  Corporate
                </Button>
              </div>
            </div>
            
            {/* Clients List */}
            <div className="space-y-3">
              {clientsData?.clients
                .filter(client => {
                  const matchesSearch = searchTerm === '' || 
                    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    client.phone?.includes(searchTerm);
                  const matchesType = clientTypeFilter === 'all' || client.client_type === clientTypeFilter;
                  return matchesSearch && matchesType;
                })
                .map((client) => (
                  <div 
                    key={client.id}
                    className={`bg-zinc-800 rounded-xl border overflow-hidden ${
                      client.client_type === 'corporate' ? 'border-purple-500/30' : 'border-zinc-700'
                    }`}
                  >
                    {/* Client Header */}
                    <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          client.client_type === 'corporate' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                        }`}>
                          {client.client_type === 'corporate' ? (
                            <Building2 className="w-6 h-6 text-purple-400" />
                          ) : (
                            <User className="w-6 h-6 text-blue-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-barlow font-bold text-white">{client.name}</p>
                          {client.company_name && (
                            <p className="text-purple-400 text-sm">{client.company_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${
                          client.client_type === 'corporate' ? 'bg-purple-600' : 'bg-blue-600'
                        } text-white border-0`}>
                          {client.client_type === 'corporate' ? 'Corporate' : 'Particular'}
                        </Badge>
                        <Badge className={`border-0 ${
                          client.purchase_frequency === 'Frequente' ? 'bg-green-600' :
                          client.purchase_frequency === 'Regular' ? 'bg-yellow-600' :
                          client.purchase_frequency === 'Ocasional' ? 'bg-orange-600' : 'bg-zinc-600'
                        } text-white`}>
                          {client.purchase_frequency}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="px-4 py-3 bg-zinc-900/50 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Mail className="w-4 h-4" />
                        <span>{client.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Phone className="w-4 h-4" />
                        <span>{client.phone || 'Sem telefone'}</span>
                      </div>
                      {client.nif && (
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <Building2 className="w-4 h-4" />
                          <span>NIF: {client.nif}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Award className="w-4 h-4 text-yellow-500" />
                        <span>{client.loyalty_points} pontos</span>
                      </div>
                    </div>
                    
                    {/* Purchase Stats */}
                    <div className="px-4 py-3 border-t border-zinc-700 flex flex-wrap gap-4">
                      <div>
                        <p className="text-zinc-500 text-xs">Pedidos</p>
                        <p className="text-white font-bold">{client.total_orders}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Concluídos</p>
                        <p className="text-green-400 font-bold">{client.completed_orders}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Total Gasto</p>
                        <p className="text-sonangol-orange font-bold">{client.total_spent.toLocaleString('pt-AO')} AOA</p>
                      </div>
                      {client.favorite_product && (
                        <div className="flex-1 min-w-[150px]">
                          <p className="text-zinc-500 text-xs">Produto Favorito</p>
                          <p className="text-white text-sm truncate">{client.favorite_product}</p>
                        </div>
                      )}
                      {client.last_order_date && (
                        <div>
                          <p className="text-zinc-500 text-xs">Última Compra</p>
                          <p className="text-zinc-300 text-sm">
                            {new Date(client.last_order_date).toLocaleDateString('pt-AO')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Reports Download Panel */}
        {activeNav === 'reports-download' && (
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-barlow font-bold text-xl text-white">Descarregar Relatórios</h2>
                <p className="text-zinc-400 text-sm">Relatórios semanais, mensais, trimestrais, semestrais e anuais</p>
              </div>
            </div>
            <ReportsPanel />
          </div>
        )}

        {/* Stock Movements Panel */}
        {activeNav === 'stock-movements' && user?.station_id && (
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <History className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-barlow font-bold text-xl text-white">Gestão Avançada de Stock</h2>
                <p className="text-zinc-400 text-sm">Movimentos, entradas, saídas e histórico completo</p>
              </div>
            </div>
            <StockManagementPanel stationId={user.station_id} />
          </div>
        )}

        {/* Mobile Bottom Nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 safe-bottom bg-zinc-800 border-t border-zinc-700 flex items-center justify-around z-50">
          <button
            onClick={() => setActiveNav('orders')}
            className={`flex flex-col items-center justify-center py-2 px-2 ${
              activeNav === 'orders' ? 'text-sonangol-orange' : 'text-zinc-500'
            }`}
          >
            <ClipboardList className="w-5 h-5" />
            <span className="text-[9px] mt-1">Pedidos</span>
          </button>
          <button
            onClick={() => setActiveNav('stock')}
            className={`flex flex-col items-center justify-center py-2 px-2 ${
              activeNav === 'stock' ? 'text-sonangol-orange' : 'text-zinc-500'
            }`}
          >
            <Boxes className="w-5 h-5" />
            <span className="text-[9px] mt-1">Stock</span>
          </button>
          <button
            onClick={() => setActiveNav('drivers')}
            className={`flex flex-col items-center justify-center py-2 px-2 relative ${
              activeNav === 'drivers' ? 'text-sonangol-orange' : 'text-zinc-500'
            }`}
          >
            <Users className="w-5 h-5" />
            <span className="text-[9px] mt-1">Livreurs</span>
            {pendingDrivers.length > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-green-500 text-white text-[8px] rounded-full flex items-center justify-center">
                {pendingDrivers.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveNav('alerts')}
            className={`flex flex-col items-center justify-center py-2 px-2 relative ${
              activeNav === 'alerts' ? 'text-sonangol-orange' : 'text-zinc-500'
            }`}
          >
            <Bell className="w-5 h-5" />
            <span className="text-[9px] mt-1">Alertas</span>
            {alerts.length > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center">
                {alerts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveNav('reports')}
            className={`flex flex-col items-center justify-center py-2 px-2 ${
              activeNav === 'reports' ? 'text-sonangol-orange' : 'text-zinc-500'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-[9px] mt-1">Rapports</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex flex-col items-center justify-center py-2 px-2 text-red-400"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[9px] mt-1">Sair</span>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default AdminDashboard;
